import type { State } from '@staiter/shared/task-module';
import {
  Circle,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleHelp,
  CircleX,
  type LucideIcon,
} from 'lucide-react';
import type { ButtonHTMLAttributes } from 'react';
import styles from './StateChip.module.css';
import { stateColorValue, stateShadeFilled } from './state-shade';

export type StateChipProps = {
  state: Pick<State, 'name' | 'color_token' | 'group'>;
  onClick?: () => void;
  size?: 'sm' | 'md';
  className?: string;
  /**
   * Дополнительные атрибуты кнопки — когда чип служит триггером поповера
   * (`aria-haspopup`/`aria-expanded`/`aria-controls`, обработчик стрелок).
   * Без этого связь «кнопка ↔ список статусов» в дереве доступности отсутствует.
   */
  buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
  /**
   * WTP-F7 (решение владельца В-13) — ступень оттенка внутри группы. Считает
   * вызывающий, потому что коллизия видна только на СПИСКЕ статусов проекта:
   * сам чип знает лишь свой статус. Ноль (умолчание) — базовый токен группы,
   * то есть поведение до правки для всех, у кого коллизии нет.
   */
  shadeIndex?: number;
};

/** FE-6 — иконка статуса по группе (форма дублирует цвет, §1.3 дальтонизм). */
const GROUP_ICON: Record<string, LucideIcon> = {
  backlog: CircleDashed,
  unstarted: Circle,
  started: CircleDot,
  completed: CircleCheck,
  cancelled: CircleX,
  triage: CircleHelp,
};

/**
 * FND-11 (гейт фазы B, раунд 4) → WTP-F7 — почему «В работе» и «На проверке»
 * выглядели одинаково и чем это вылечено.
 *
 * Глиф берётся по ГРУППЕ статуса, а групп ровно шесть (см. `StateGroup`), и
 * «на проверке» — не группа: это пользовательский статус внутри `started`.
 * Ключ `review` здесь был бы мёртвой веткой — такого значения в перечислении
 * нет. Различать их обязан цвет, а на стенде обоим выдан один токен.
 *
 * Решение владельца В-13 (28.08.2026): не заводить поле иконки у статуса
 * (правка модели отложена как улучшение по запросу пользователей), а дать
 * заранее заданную палитру ОТТЕНКОВ — статусы одной группы с одинаковым
 * токеном получают разные оттенки автоматически, по порядку. Правило и
 * его разбор — в `state-shade.ts`; сюда приезжает готовая ступень.
 */

/**
 * StateChip — цветной chip статуса задачи (FE-S12.1-6).
 *
 * Цвет берётся из --column-color-{token} (12 hues palette, UI §I.26 / FE-S12.1-1).
 * FE-6 — форма-иконка по группе (не только цвет-точка): §1.3 дальтонизм.
 */
export function StateChip({
  state,
  onClick,
  size = 'md',
  className = '',
  buttonProps,
  shadeIndex = 0,
}: StateChipProps): JSX.Element {
  const classes = [styles.chip, styles[`size_${size}`], onClick ? styles.clickable : '', className]
    .filter(Boolean)
    .join(' ');

  const Icon = GROUP_ICON[state.group] ?? Circle;
  /**
   * WTP-F8 — закреплённый оттенок статуса имеет приоритет над переданным
   * порядковым. Так чип красится верно даже там, где вызывающий про оттенок
   * ничего не знает и ничего не передаёт.
   */
  const effectiveShade =
    typeof (state as { shade_index?: number }).shade_index === 'number'
      ? ((state as { shade_index?: number }).shade_index as number)
      : shadeIndex;
  const iconColor = stateColorValue(state.color_token, effectiveShade);
  // F10.1 — defensive fallback: если имя статуса пустое (битый/незагруженный state) —
  // нейтральный «—» вместо raw-плейсхолдера/глитча. Данные-кейс (mojibake) чинит B10.1.
  const label = state.name?.trim() || '—';
  // FE-6 ui-rework — крупнее (13/15px) + тоньше stroke: внутренний глиф формы
  // (галочка/точка/штрих) читается на compact-размере, не тонет в кольце (§1.3).
  const icon = (
    <Icon
      size={size === 'sm' ? 13 : 15}
      strokeWidth={1.75}
      className={styles.icon}
      /* WTP-F7, раунд 1 гейта: различие статусов внутри группы несут ДВА канала —
         светлота оттенка и форма глифа (контурный против залитого). Одного цвета
         на глифе 13px гейту не хватило, и он прав: «на заливке 12–15% alpha
         остаётся ~0.008 L». */
      {...(stateShadeFilled(effectiveShade) ? { fill: 'currentColor', fillOpacity: 0.35 } : {})}
      style={{ color: iconColor }}
      aria-hidden="true"
    />
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        onClick={onClick}
        aria-label={label}
        {...buttonProps}
      >
        {icon}
        <span className={styles.label}>{label}</span>
      </button>
    );
  }
  return (
    <span className={classes}>
      {icon}
      <span className={styles.label}>{label}</span>
    </span>
  );
}
